/**
 * ToolPermissionsPage — Landlord / Manager
 *
 * Route: /settings/tool-permissions
 *
 * Displays per-client tool permission management. For each active MCP grant,
 * shows the list of available tools grouped by consentGroup, allows the user
 * to toggle individual tools, and saves with a bulk replace (PUT).
 *
 * Rendered inside SettingsLayout.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ScopeList, TOOL_TO_GROUP, isToolMutating } from '@resida/consent-ui';
import type { ConsentScopeItem } from '@resida/consent-ui';
import {
  listMcpGrants,
  listGrantPermissions,
  replaceGrantPermissions,
  type GrantRecord,
  type ToolPermissionRecord,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { SettingsLayout } from '../components/settings/SettingsLayout';


interface ClientPermissionsState {
  grant: GrantRecord;
  permissions: ToolPermissionRecord[];
  selectedToolNames: string[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  saveSuccess: boolean;
}

export function ToolPermissionsPage() {
  const [grants, setGrants] = useState<GrantRecord[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [clientStates, setClientStates] = useState<Record<string, ClientPermissionsState>>({});

  const loadGrants = useCallback(async () => {
    try {
      setPageLoading(true);
      setPageError(null);
      const data = await listMcpGrants();
      setGrants(data.grants);
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load connected apps');
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGrants();
  }, [loadGrants]);

  useEffect(() => {
    if (grants.length === 0) return;
    // Fetch permissions for each grant
    grants.forEach((grant) => {
      setClientStates((prev) => ({
        ...prev,
        [grant.clientId]: {
          grant,
          permissions: [],
          selectedToolNames: [],
          loading: true,
          saving: false,
          error: null,
          saveSuccess: false,
        },
      }));

      listGrantPermissions(grant.clientId)
        .then((data) => {
          setClientStates((prev) => {
            const existing = prev[grant.clientId];
            if (!existing) return prev;
            return {
              ...prev,
              [grant.clientId]: {
                ...existing,
                permissions: data.permissions,
                selectedToolNames: data.permissions.map((p) => p.toolName),
                loading: false,
              } satisfies ClientPermissionsState,
            };
          });
        })
        .catch((err) => {
          setClientStates((prev) => {
            const existing = prev[grant.clientId];
            if (!existing) return prev;
            return {
              ...prev,
              [grant.clientId]: {
                ...existing,
                loading: false,
                error: err instanceof Error ? err.message : 'Failed to load permissions',
              } satisfies ClientPermissionsState,
            };
          });
        });
    });
  }, [grants]);

  function handleToggle(clientId: string, toolName: string, checked: boolean) {
    setClientStates((prev) => {
      const state = prev[clientId];
      if (!state) return prev;
      const selectedToolNames = checked
        ? [...state.selectedToolNames, toolName]
        : state.selectedToolNames.filter((n) => n !== toolName);
      const updated: ClientPermissionsState = { ...state, selectedToolNames, saveSuccess: false };
      return { ...prev, [clientId]: updated };
    });
  }

  async function handleSave(clientId: string) {
    const state = clientStates[clientId];
    if (!state) return;

    setClientStates((prev) => {
      const existing = prev[clientId];
      if (!existing) return prev;
      return {
        ...prev,
        [clientId]: { ...existing, saving: true, error: null, saveSuccess: false } satisfies ClientPermissionsState,
      };
    });

    try {
      await replaceGrantPermissions(clientId, state.selectedToolNames);
      setClientStates((prev) => {
        const existing = prev[clientId];
        if (!existing) return prev;
        return {
          ...prev,
          [clientId]: { ...existing, saving: false, saveSuccess: true } satisfies ClientPermissionsState,
        };
      });
    } catch (err) {
      setClientStates((prev) => {
        const existing = prev[clientId];
        if (!existing) return prev;
        return {
          ...prev,
          [clientId]: {
            ...existing,
            saving: false,
            error: err instanceof Error ? err.message : 'Failed to save permissions',
          } satisfies ClientPermissionsState,
        };
      });
    }
  }

  return (
    <SettingsLayout>
      <div
        data-testid="tool-permissions-page"
        className="px-[var(--page-padding-x)]"
      >
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-foreground m-0">Tool Permissions</h1>
            <p className="text-sm text-muted-foreground mt-1 mb-0">
              Manage which tools each connected app can use.
            </p>
          </div>

          {pageError && (
            <div
              role="alert"
              className="flex justify-between items-center p-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-6"
            >
              <p className="text-sm text-destructive m-0">{pageError}</p>
              <Button variant="outline" size="sm" onClick={loadGrants} className="min-h-[44px]">
                Retry
              </Button>
            </div>
          )}

          {pageLoading ? (
            <div
              data-testid="tool-permissions-loading"
              role="status"
              aria-live="polite"
              className="space-y-3"
            >
              {[1, 2].map((i) => (
                <div key={i} className="h-32 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : grants.length === 0 ? (
            <div
              data-testid="tool-permissions-empty"
              className="text-center py-10 px-6 bg-card rounded-lg border border-border"
            >
              <h2 className="text-lg font-semibold text-foreground m-0 mb-2">No Connected Apps</h2>
              <p className="text-sm text-muted-foreground m-0 mb-4">
                No connected apps yet. Connect an app via the MCP consent flow first.
              </p>
              <Button variant="outline" asChild>
                <Link to="/settings/connected-apps">Go to Connected Apps</Link>
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {grants.map((grant) => {
                const state = clientStates[grant.clientId];
                const noToolsSelected = state ? state.selectedToolNames.length === 0 : false;

                return (
                  <div
                    key={grant.clientId}
                    data-testid={`client-permissions-${grant.clientId}`}
                    className="bg-card rounded-lg border border-border p-5"
                  >
                    {/* Client header */}
                    <div className="flex items-center gap-3 mb-4">
                      {grant.logoUri ? (
                        <img
                          src={grant.logoUri}
                          alt={`${grant.clientName} logo`}
                          className="w-8 h-8 rounded object-contain flex-shrink-0"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-muted rounded flex items-center justify-center text-xs font-semibold text-muted-foreground flex-shrink-0">
                          {grant.clientName.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <h2 className="m-0 text-base font-semibold text-foreground">
                        {grant.clientName}
                      </h2>
                    </div>

                    {state?.loading ? (
                      <div className="h-16 bg-muted animate-pulse rounded" />
                    ) : state?.error ? (
                      <p className="text-sm text-destructive">{state.error}</p>
                    ) : state && state.permissions.length > 0 ? (
                      <>
                        <ScopeList
                          scopes={state.permissions.map((p) => ({
                            name: p.toolName,
                            displayName: p.toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                            description: '',
                            category: TOOL_TO_GROUP[p.toolName] ?? 'other',
                            consentGroup: TOOL_TO_GROUP[p.toolName] ?? 'other',
                            mutating: isToolMutating(p.toolName),
                          } as ConsentScopeItem))}
                          selectedScopes={state.selectedToolNames}
                          onToggle={(toolName, checked) => handleToggle(grant.clientId, toolName, checked)}
                          disabled={state.saving}
                          groupMode={true}
                        />

                        {/* At-least-one warning (INV-MCP-14) */}
                        {noToolsSelected && (
                          <p
                            data-testid="permissions-at-least-one-warning"
                            role="alert"
                            className="text-sm text-destructive mt-2"
                          >
                            At least one tool must remain selected.
                          </p>
                        )}

                        {state.saveSuccess && (
                          <p data-testid="success-message" className="text-sm text-success mt-2" role="status">
                            Permissions saved.
                          </p>
                        )}

                        {state.error && (
                          <p className="text-sm text-destructive mt-2" role="alert">
                            {state.error}
                          </p>
                        )}

                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                          <p className="text-xs text-muted-foreground m-0">
                            To stop using this client entirely, revoke it in{' '}
                            <Link to="/settings/connected-apps" className="underline">
                              Connected Apps
                            </Link>
                            .
                          </p>
                          <Button
                            data-testid={`save-permissions-${grant.clientId}`}
                            size="sm"
                            onClick={() => handleSave(grant.clientId)}
                            disabled={state.saving || noToolsSelected}
                            className="min-h-[44px]"
                          >
                            {state.saving ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No tools are currently authorized for this client.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </SettingsLayout>
  );
}

export default ToolPermissionsPage;
